import { useCallback } from 'react'
import Papa from 'papaparse'
import { TableColumn } from '@/components/Table/GenericTable'

interface UseCsvDownloadOptions<T> {
  data: T[]
  columns: TableColumn<T>[]
  fileName?: string
}

export function useCsvDownload<T>({ data, columns, fileName = 'data.csv' }: UseCsvDownloadOptions<T>) {
  const downloadCsv = useCallback(async () => {
    try {
      const visibleColumns = columns.filter(col => col.visible)

      const csvData = data.map(item => {
        const row: Record<string, string> = {}
        visibleColumns.forEach(col => {
          const value = col.csvValue ? col.csvValue(item, col.key) : item[col.key]
          if (value === null || value === undefined) {
            row[col.label] = ''
          } else if (typeof value === 'object') {
            if (value && typeof value === 'object' && 'content' in value && value.content !== undefined) {
              row[col.label] = String(value.content)
            } else if (Array.isArray(value)) {
              row[col.label] = value.join(', ')
            } else {
              row[col.label] = JSON.stringify(value)
            }
          } else {
            row[col.label] = String(value)
          }
        })
        return row
      })

      const csv = Papa.unparse({
        fields: visibleColumns.map(col => col.label),
        data: csvData,
      })

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', fileName)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      alert('CSV download failed. Please try again or use a modern browser.')
    }
  }, [data, columns, fileName])

  return { downloadCsv }
}
